import * as Dialog from '@radix-ui/react-dialog';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Close } from '@/components/Icons/Close';
import { Download } from '@/components/Icons/Download';
import { PdfPreview } from '@/components/PdfPreview';
import { PageSpinner } from '@/components/Spinner/PageSpinner';
import { useCloseOnBack } from '@/hooks/useCloseOnBack';
import { hasPdfExtension } from '@/utils/file';

interface Props {
  name: string;
  url?: string;
  onClose: () => void;
  onDownload: () => void;
}

export const AttachmentPreviewModal: React.FC<Props> = ({
  name,
  url,
  onClose,
  onDownload,
}) => {
  const { t } = useTranslation();
  const isPdf = hasPdfExtension(name);

  useCloseOnBack(true, onClose);

  return (
    <Dialog.Root open onOpenChange={(isOpen) => !isOpen && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[70] bg-black/50" />
        <Dialog.Content
          aria-describedby={undefined}
          onClick={(event) => {
            if (event.target === event.currentTarget) {
              onClose();
            }
          }}
          className="fixed inset-0 z-[70] flex items-center justify-center bg-[#131313] outline-none md:bg-transparent md:p-4"
        >
          <Dialog.Title className="sr-only">{name}</Dialog.Title>

          <div className="absolute inset-x-0 top-0 z-10 grid grid-cols-[1fr_auto_1fr] items-center gap-3 px-4 pt-[calc(16px+env(safe-area-inset-top))] md:hidden">
            <button
              type="button"
              onClick={onDownload}
              disabled={!url}
              aria-label={t('home.newsDetail.downloadLabel')}
              className="flex size-6 cursor-pointer items-center justify-center text-white disabled:opacity-50"
            >
              <Download className="size-6" />
            </button>
            <p className="min-w-0 truncate text-center text-subhead-14 text-white">
              {name}
            </p>
            <button
              type="button"
              onClick={onClose}
              aria-label={t('global.close')}
              className="flex size-6 cursor-pointer items-center justify-center justify-self-end text-white"
            >
              <Close className="size-6" />
            </button>
          </div>

          <div className="flex size-full max-h-full max-w-full flex-col md:size-auto md:w-full md:max-w-[1052px] md:gap-4 md:rounded-2xl md:bg-card md:p-5 md:pb-9 md:shadow-modal">
            <header className="hidden grid-cols-[1fr_auto_1fr] items-center gap-3 md:grid">
              <button
                type="button"
                onClick={onDownload}
                disabled={!url}
                className="inline-flex w-fit shrink-0 cursor-pointer items-center gap-1.5 rounded-2xl bg-info-surface px-3 py-2 text-caption-13 text-primary disabled:opacity-50"
              >
                <Download className="size-4" />
                {t('home.newsDetail.downloadLabel')}
              </button>
              <p className="min-w-0 truncate text-center text-subhead-14 text-foreground">
                {name}
              </p>
              <button
                type="button"
                onClick={onClose}
                aria-label={t('global.close')}
                className="flex size-6 shrink-0 cursor-pointer items-center justify-self-end text-muted-foreground"
              >
                <Close className="size-5" />
              </button>
            </header>

            <div className="flex h-full items-center justify-center overflow-hidden pt-[calc(56px+env(safe-area-inset-top))] md:h-[75vh] md:rounded-xl md:bg-card md:pt-0">
              {!url && <PageSpinner />}
              {url && isPdf && <PdfPreview url={url} title={name} />}
              {url && !isPdf && (
                <img
                  src={url}
                  alt={name}
                  draggable={false}
                  className="size-full object-contain select-none"
                />
              )}
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
